import { useState, useEffect } from 'react';
import { EOLBadge } from './EOLBadge';

interface EOLCycle {
  cycle: string;
  release_label?: string;
  release_date?: string;
  support_date?: string;
  eol_date?: string;
  is_active: boolean;
  is_lts: boolean;
  extended_support?: boolean;
  latest_version?: string;
  link?: string;
}

interface EOLStatusPanelProps {
  productId: number;
  productType: 'software' | 'os';
  className?: string;
}

interface EOLData {
  hasEOLData: boolean;
  message?: string;
  cycles?: EOLCycle[];
}

export function EOLStatusPanel({
  productId,
  productType,
  className = '',
}: EOLStatusPanelProps) {
  const [eolData, setEOLData] = useState<EOLData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEOLData = async () => {
      try {
        setLoading(true);
        setError(null);

        const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
        const endpoint =
          productType === 'os'
            ? `/api/v1/os/products/${productId}/eol`
            : `/api/v1/software/${productId}/eol`;

        const response = await fetch(`${apiUrl}${endpoint}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch EOL data: ${response.statusText}`);
        }

        const result = await response.json();

        if (result.success) {
          setEOLData(result.data);
        } else {
          throw new Error(result.error || 'Failed to load EOL data');
        }
      } catch (err) {
        console.error('Error fetching EOL data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchEOLData();
  }, [productId, productType]);

  if (loading) {
    return (
      <div className={className}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-400">Loading EOL information...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={className}>
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
          <p className="text-red-400">
            <strong>Error:</strong> {error}
          </p>
        </div>
      </div>
    );
  }

  if (!eolData?.hasEOLData) {
    return (
      <div className={className}>
        <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
          <p className="text-blue-400">
            <strong>No EOL tracking:</strong> {eolData?.message || 'EOL information not available'}
          </p>
        </div>
      </div>
    );
  }

  // Get the current/latest cycle (first one with is_active or most recent)
  const currentCycle = eolData.cycles?.find((c) => c.is_active) || eolData.cycles?.[0];

  // Show up to 5 most recent cycles
  const recentCycles = eolData.cycles?.slice(0, 5) || [];

  return (
    <div className={className}>
      <h3 className="text-xl font-semibold text-gray-900 mb-4">End of Life Status</h3>

      {currentCycle && (
        <div className="mb-6">
          <div className="flex justify-between items-start mb-3">
            <div>
              <h4 className="text-lg font-semibold text-gray-900">
                Cycle: {currentCycle.release_label || currentCycle.cycle}
                {Boolean(currentCycle.is_lts) && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 font-semibold border border-blue-200">
                    LTS
                  </span>
                )}
              </h4>
            </div>
            <EOLBadge
              eolDate={currentCycle.eol_date || null}
              supportDate={currentCycle.support_date || null}
              isActive={currentCycle.is_active}
              isLTS={currentCycle.is_lts}
              extendedSupport={currentCycle.extended_support}
            />
          </div>

          {(currentCycle.release_date || currentCycle.support_date || currentCycle.eol_date || currentCycle.latest_version) && (
            <div className="bg-gray-100 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {currentCycle.release_date && (
                  <div>
                    <p className="text-sm text-gray-600">Release Date</p>
                    <p className="text-gray-900 font-medium">
                      {new Date(currentCycle.release_date).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {currentCycle.support_date && (
                  <div>
                    <p className="text-sm text-gray-600">Support Ends</p>
                    <p className="text-gray-900 font-medium">
                      {new Date(currentCycle.support_date).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {currentCycle.eol_date && (
                  <div>
                    <p className="text-sm text-gray-600">End of Life</p>
                    <p className="text-gray-900 font-medium">
                      {new Date(currentCycle.eol_date).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {currentCycle.latest_version && (
                  <div>
                    <p className="text-sm text-gray-600">Latest Version</p>
                    <p className="text-gray-900 font-medium">{currentCycle.latest_version}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {currentCycle.link && !currentCycle.link.includes('__LATEST__') && !currentCycle.link.includes('__') && (
            <div className="mt-3">
              <a
                href={currentCycle.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 text-sm"
              >
                View official release information →
              </a>
            </div>
          )}
        </div>
      )}

      {recentCycles.length > 1 && (
        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-3">Release Cycles</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Cycle
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Release Date
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    EOL Date
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentCycles.map((cycle, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900">
                          {cycle.release_label || cycle.cycle}
                        </span>
                        {Boolean(cycle.is_lts) && (
                          <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 border border-blue-200">
                            LTS
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {cycle.release_date ? new Date(cycle.release_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {cycle.eol_date ? new Date(cycle.eol_date).toLocaleDateString() : 'Supported'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <EOLBadge
                        eolDate={cycle.eol_date || null}
                        supportDate={cycle.support_date || null}
                        isActive={cycle.is_active}
                        isLTS={cycle.is_lts}
                        extendedSupport={cycle.extended_support}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-4 text-sm text-gray-600">
        Data provided by{' '}
        <a
          href="https://endoflife.date"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-700"
        >
          endoflife.date
        </a>
      </div>
    </div>
  );
}

export default EOLStatusPanel;
